import { useQuery } from '@tanstack/react-query';
import type { Version } from '@/generated/api/models/version';
import type { VersionChange } from '@/generated/api/models/versionChange';
import type { VersionListResponse } from '@/generated/api/models/versionListResponse';
import type { VersionCompareResponse } from '@/generated/api/models/versionCompareResponse';
import type { VersionSnapshotResponse } from '@/generated/api/models/versionSnapshotResponse';
import axiosInstance from '../../../api/axiosInstance';

/**
 * The version-history wire types, re-exported straight from the generated client.
 *
 * They used to be hand-written here behind two `typed-surface-ok:` escapes:
 * `modules/shared/api/version_routes.py` declared its response models INLINE with
 * `data: List[Dict[str, Any]]`, so the generated `VersionListResponse.data` was
 * `{ [key: string]: unknown }[]` and `VersionCompareResponse` erased all three of
 * its members — adopting either would have made `version_number`, `changed_at`,
 * `change_type`, `revision_number` and `changes` `unknown` across `VersionTimeline`,
 * `VersionDetail` and `VersionCompare`. Those models now live in
 * `shared/schemas/shared/version*.schema.json` with a real row schema, so the shape
 * is generated and there is nothing left to restate.
 */
export type { Version, VersionChange, VersionListResponse, VersionCompareResponse };

async function fetchVersionHistory(
  objectType: string,
  objectId: string,
  limit: number,
  offset: number
): Promise<VersionListResponse> {
  const response = await axiosInstance.get<VersionListResponse>(
    `/api/v1/versions/${objectType}/${objectId}`,
    { params: { limit, offset } }
  );
  return response.data;
}

async function fetchVersionAtTime(
  objectType: string,
  objectId: string,
  timestamp: string
): Promise<VersionSnapshotResponse> {
  const response = await axiosInstance.get<VersionSnapshotResponse>(
    `/api/v1/versions/${objectType}/${objectId}/at`,
    { params: { timestamp } }
  );
  return response.data;
}

async function compareVersions(
  objectType: string,
  objectId: string,
  fromVersion: number,
  toVersion: number
): Promise<VersionCompareResponse> {
  const response = await axiosInstance.get<VersionCompareResponse>(
    `/api/v1/versions/${objectType}/${objectId}/diff`,
    { params: { from: fromVersion, to: toVersion } }
  );
  return response.data;
}

export const versionKeys = {
  all: ['versions'] as const,
  history: (objectType: string, objectId: string) =>
    [...versionKeys.all, 'history', objectType, objectId] as const,
  historyPaged: (objectType: string, objectId: string, limit: number, offset: number) =>
    [...versionKeys.history(objectType, objectId), { limit, offset }] as const,
  compare: (objectType: string, objectId: string, from: number, to: number) =>
    [...versionKeys.all, 'compare', objectType, objectId, from, to] as const,
  atTime: (objectType: string, objectId: string, timestamp: string) =>
    [...versionKeys.all, 'at', objectType, objectId, timestamp] as const,
};

export function useVersionHistory(
  objectType: string,
  objectId: string,
  options?: { limit?: number; offset?: number; enabled?: boolean }
) {
  const { limit = 20, offset = 0, enabled = true } = options ?? {};

  return useQuery({
    queryKey: versionKeys.historyPaged(objectType, objectId, limit, offset),
    queryFn: () => fetchVersionHistory(objectType, objectId, limit, offset),
    enabled: enabled && !!objectType && !!objectId,
  });
}

export function useVersionCompare(
  objectType: string,
  objectId: string,
  fromVersion: number,
  toVersion: number,
  options?: { enabled?: boolean }
) {
  const { enabled = true } = options ?? {};

  return useQuery({
    queryKey: versionKeys.compare(objectType, objectId, fromVersion, toVersion),
    queryFn: () => compareVersions(objectType, objectId, fromVersion, toVersion),
    enabled: enabled && !!objectType && !!objectId && fromVersion > 0 && toVersion > 0,
  });
}

export function useVersionAtTime(
  objectType: string,
  objectId: string,
  timestamp: string,
  options?: { enabled?: boolean }
) {
  const { enabled = true } = options ?? {};

  return useQuery({
    queryKey: versionKeys.atTime(objectType, objectId, timestamp),
    queryFn: () => fetchVersionAtTime(objectType, objectId, timestamp),
    enabled: enabled && !!objectType && !!objectId && !!timestamp,
  });
}
